import { ResponsiveDialog } from "Components/Shared/Dialogs/ResponsiveDialog";
import {
  type StepDirection,
  StepTransition,
} from "Components/Shared/Transitions/StepTransition";
import { ContentReportFooter } from "Components/Support/ContentReport/ContentReportFooter";
import type { ContentReportFormData } from "Components/Support/ContentReport/contentReportSchema";
import type { ContentReportStep } from "Components/Support/ContentReport/ContentReportStep";
import { ContentReportStepContent } from "Components/Support/ContentReport/ContentReportStepContent";
import type { ContentReportTarget } from "Components/Support/ContentReport/ContentReportTarget";
import type { SupportFlowResult } from "Components/Support/SupportFlowResult";
import type { SupportFormStatus } from "Components/Support/SupportFormStatus";
import { useFormModalClose } from "Hooks/useFormModalClose";
import { Resources, useResource } from "Translations/Resources";
import { useCallback, useState } from "react";

const RESOURCES = Resources.Support.ContentReport;

const CLEAN_FORM_STATUS: SupportFormStatus = { isDirty: false, isBusy: false };

type Props = {
  isOpen: boolean;
  target: ContentReportTarget;
  onClose: () => void;
};

/**
 * The notice dialog opened from a profile's ⋮ menu or a chat message's actions — the target is
 * already known, so the flow is just form → result. The Support button's picker reaches the same
 * form through SupportFlowDialog instead. State is not reset on close; hosts remount this on every
 * open (key it), so the closing dialog doesn't snap back to the form mid-fade.
 */
export const ContentReportDialog = ({ isOpen, target, onClose }: Props) => {
  const { t } = useResource();
  const [step, setStep] = useState<ContentReportStep>("form");
  const [direction, setDirection] = useState<StepDirection>("forward");
  const [result, setResult] = useState<SupportFlowResult | null>(null);
  const [formStatus, setFormStatus] =
    useState<SupportFormStatus>(CLEAN_FORM_STATUS);
  const [retryValues, setRetryValues] = useState<
    ContentReportFormData | undefined
  >(undefined);

  const isFormStep = step === "form";
  const hasUnsavedInput = isFormStep && formStatus.isDirty;
  const isSubmitInFlight = isFormStep && formStatus.isBusy;
  // A result that cannot be retried has nothing to act on, so that step gets no action bar.
  const hasFooter = isFormStep || !!result?.canRetry;

  const { handleCloseRequest, ConfirmDialogComponent } = useFormModalClose({
    isDirty: hasUnsavedInput,
    onClose,
  });

  const handleSubmitted = (
    submittedResult: SupportFlowResult,
    submittedValues: ContentReportFormData,
  ) => {
    setResult(submittedResult);
    setRetryValues(submittedValues);
    setDirection("forward");
    setStep("result");
  };

  const handleRetry = () => {
    setResult(null);
    setFormStatus(CLEAN_FORM_STATUS);
    setDirection("backward");
    setStep("form");
  };

  const handleStatusChange = useCallback(
    (status: SupportFormStatus) => setFormStatus(status),
    [],
  );

  return (
    <>
      <ResponsiveDialog
        isOpen={isOpen}
        title={isFormStep ? t(RESOURCES.Title) : undefined}
        accessibilityLabel={isFormStep ? undefined : t(RESOURCES.Title)}
        onClose={handleCloseRequest}
        footer={
          hasFooter ? (
            <ContentReportFooter
              step={step}
              isSubmitInFlight={isSubmitInFlight}
              onCancel={handleCloseRequest}
              onRetry={handleRetry}
            />
          ) : undefined
        }
      >
        <StepTransition
          stepKey={step}
          direction={direction}
          renderStep={(currentStep) => (
            <ContentReportStepContent
              step={currentStep}
              target={target}
              result={result}
              retryValues={retryValues}
              onStatusChange={handleStatusChange}
              onSubmitted={handleSubmitted}
            />
          )}
        />
      </ResponsiveDialog>
      {ConfirmDialogComponent}
    </>
  );
};
